import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  useCreatePaymentMethodV2,
  useFetchEntidadesFinancieras,
  useFetchPaymentMethodsParams,
  useUpdatePaymentMethodV2,
} from '@/shared/actions/erp';
import {
  CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomTextField,
  SampleCheckbox,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import {
  IPaymentMethodV2,
  TipoPagoV2Enum,
  TIPO_PAGO_V2_OPTIONS,
} from '@/shared/interfaces/erp/system-settings';
import { gridSizeMdLg3 } from '@/shared';
import { EntidadesFinancieras, IPaymentMethod } from '@/shared/interfaces';

export type SavePaymentMethodV2Props = {
  editPaymentMethod?: IPaymentMethodV2;
  titlePage: string;
};

type SaveFormData = {
  codigo: string;
  numero_referencia: string;
  nombre: string;
  tipo_pago: TipoPagoV2Enum | string;
  entidad_financiera: string;
  forma_pago_legacy: string | null; // FK a FormaPago V1
  aplica_home: boolean;
  aplica_pyme: boolean;
  aplica_corporativo: boolean;
  estado: boolean;
};

const paymentMethodV2Schema = yup.object().shape({
  codigo: yup.string().required('El código es requerido'),
  numero_referencia: yup.string().required('El número de referencia es requerido'),
  nombre: yup.string().required('El nombre es requerido'),
  tipo_pago: yup.string().required('El tipo de pago es requerido'),
  entidad_financiera: yup.string().when('tipo_pago', {
    is: (tipo_pago: string) => tipo_pago === TipoPagoV2Enum.RECAUDACION,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('La entidad financiera es requerida'),
  }),
  forma_pago_legacy: yup.string().nullable().required('La forma de pago V1 es requerida'),
  aplica_home: yup.boolean(),
  aplica_pyme: yup.boolean(),
  aplica_corporativo: yup.boolean(),
  estado: yup.boolean(),
});

const returnUrlPayment = '/parametrizacion/metodo-pago';

const SavePaymentMethodV2: React.FC<SavePaymentMethodV2Props> = ({
  titlePage,
  editPaymentMethod,
}) => {
  const navigate = useNavigate();

  const [shrink, setShrink] = useState(false);

  ///* mutations
  const createPaymentMethodMutation = useCreatePaymentMethodV2({
    navigate,
    returnUrl: returnUrlPayment,
  });
  const updatePaymentMethodMutation = useUpdatePaymentMethodV2({
    navigate,
    returnUrl: returnUrlPayment,
  });

  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(paymentMethodV2Schema) as any,
    defaultValues: {
      estado: true,
      aplica_home: true,
      aplica_pyme: false,
      aplica_corporativo: false,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* fetch data
  const { data: entidadesFinancierasPaging, isLoading: isLoadingEntidadesFinancieras } =
    useFetchEntidadesFinancieras({
      page_size: 600,
    });

  // Fetch formas de pago V1 para el mapeo
  const { data: paymentMethodsV1Paging, isLoading: isLoadingPaymentMethodsV1 } =
    useFetchPaymentMethodsParams({
      page_size: 600,
    });

  ///* effects
  useEffect(() => {
    if (!editPaymentMethod?.id_formapago_v2) return;
    setShrink(true);
    reset({ ...editPaymentMethod } as any);
  }, [editPaymentMethod]);

  ///* handlers
  const onChangeEntidadFinanciera = (value: string) => {
    form.setValue('entidad_financiera', value);
  };

  const onChangeTipoPago = (value: string) => {
    form.setValue('tipo_pago', value);
    // Limpiar entidad al cambiar tipo de pago
    form.setValue('entidad_financiera', '');
    // Forzar revalidación del formulario
    form.trigger('entidad_financiera');
  };

  // Watch para controlar visibilidad de campos
  const tipoPagoSeleccionado = form.watch('tipo_pago');
  const esRecaudacion = tipoPagoSeleccionado === TipoPagoV2Enum.RECAUDACION;

  const onChangeFormaPagoLegacy = (value: string) => {
    form.setValue('forma_pago_legacy', value);
  };

  const onSave = async () => {
    if (!isValid) return;

    ///* edit
    if (editPaymentMethod?.id_formapago_v2) {
      return updatePaymentMethodMutation.mutate({
        id: editPaymentMethod.id_formapago_v2,
        data: form.getValues() as any,
      });
    }

    ///* create
    return createPaymentMethodMutation.mutate(form.getValues() as any);
  };

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlPayment);
      }}
      onSave={handleSubmit(onSave)}
    >
      <CustomTextField
        label="Código"
        name="codigo"
        control={form.control}
        error={errors.codigo}
        helperText={errors.codigo?.message}
        shrink={shrink}
      />

      <CustomTextField
        label="Número de Referencia"
        name="numero_referencia"
        control={form.control}
        error={errors.numero_referencia}
        helperText={errors.numero_referencia?.message}
        shrink={shrink}
      />

      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        shrink={shrink}
      />

      <CustomAutocompleteArrString
        label="Tipo de Pago"
        name="tipo_pago"
        control={form.control}
        defaultValue={form.getValues().tipo_pago || ''}
        options={TIPO_PAGO_V2_OPTIONS}
        isLoadingData={false}
        error={errors.tipo_pago}
        helperText={errors.tipo_pago?.message}
        onChangeValue={onChangeTipoPago}
      />

      {/* Entidad Financiera - No requerida para RECAUDACIÓN */}
      {!esRecaudacion && (
        <CustomAutocomplete<EntidadesFinancieras>
          label="Entidad Financiera"
          name="entidad_financiera_autocomplete"
          options={entidadesFinancierasPaging?.data.data || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().entidad_financiera || ''}
          optionLabelForEdit={form.getValues().entidad_financiera || ''}
          isLoadingData={isLoadingEntidadesFinancieras}
          control={form.control}
          error={errors.entidad_financiera}
          helperText={errors.entidad_financiera?.message}
          onChangeValue={onChangeEntidadFinanciera}
        />
      )}

      <CustomAutocomplete<IPaymentMethod>
        label="Forma de Pago V1 (Legacy)"
        name="forma_pago_legacy_autocomplete"
        options={paymentMethodsV1Paging?.data.data || []}
        valueKey="alias"
        actualValueKey="codigo"
        defaultValue={form.getValues().forma_pago_legacy || ''}
        optionLabelForEdit={editPaymentMethod?.forma_pago_legacy_data?.alias || form.getValues().forma_pago_legacy || ''}
        isLoadingData={isLoadingPaymentMethodsV1}
        control={form.control}
        error={errors.forma_pago_legacy}
        helperText={errors.forma_pago_legacy?.message || 'Seleccionar forma de pago V1 para mapeo de compatibilidad'}
        onChangeValue={onChangeFormaPagoLegacy}
      />

      <SampleCheckbox
        label="Aplicar HOME"
        name="aplica_home"
        control={form.control}
        defaultValue={true}
        size={gridSizeMdLg3}
      />

      <SampleCheckbox
        label="Aplicar PYME"
        name="aplica_pyme"
        control={form.control}
        defaultValue={false}
        size={gridSizeMdLg3}
      />

      <SampleCheckbox
        label="Aplicar Corporativo"
        name="aplica_corporativo"
        control={form.control}
        defaultValue={false}
        size={gridSizeMdLg3}
      />

      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
      />
    </SingleFormBoxScene>
  );
};

export default SavePaymentMethodV2;
